import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Step,
  StepButton,
  Stepper,
  Typography,
  LinearProgress,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Formik } from "formik";
import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { styles } from "./styles";
import TutorialModal from "containers/Project/components/Create/components/TutorialModal";
import ConfirmDialog from "components/ConfirmDialog";
import { LoadingButton } from "@mui/lab";
import DeleteIcon from "@mui/icons-material/Delete";
import { useCreateProjectMutation } from "api/project/mutations/useCreateProjectMutation";
import UploadAssets from "./UploadAssets";
import ProjectDetailsForm from "./ProjectDetailsForm";
import AddFloors from "./AddFloors";
import * as Yup from "yup";
import { useNewProjectLocationGeocoder } from "hooks/useNewProjectLocationGeocoder";
import {
  DEFAULT_LOCATION,
  floorplanFilesAtom,
  isUploadingProjectAssetsAtom,
  Location,
  newProjectAtom,
  newProjectFloorsAtom,
  newProjectUppyInstanceAtom,
  uploadProgressAtom,
  UploadProgress,
} from "store/atoms/NewProject/newProjectAtoms";
import { useWorkspaces } from "contexts/WorkspacesContext";
import { useAtom } from "jotai";
import { toast } from "react-toastify";
import FullWidthBanner from "components/FullWidthBanner";
import { usePostHog } from "posthog-js/react";

type Props = {};

// constants
// TODO: move them to some other place maybe
const STEPS = [
  { id: "details", label: "Project Details" },
  // { id: "add-floors", label: "Add Floors" },
  // { id: "upload-assets", label: "Upload Assets" },
] as const;

export const ALLOWED_SPACE_TYPES = [
  "residential",
  "office",
  "institution",
  "others",
] as const;
export const ALLOWED_PROJECT_TYPES = [
  "under construction",
  "renovation",
  "survey sites",
  "others",
] as const;
export const ALLOWED_PROJECT_SCOPES = [
  "Visual documentation / Progress Tracking",
  "Virtual Tour for Presentation / Marketing",
  "Point cloud Scanning",
  "Architectural As Built",
  "Architectural + MEP As Built",
  "Architectural + MEP + Furnishing As Built",
  "Interior Design",
  "Object Detection Analytics",
] as const;
export const ALLOWED_UNIT_SYSTEMS = ["imperial", "metric"] as const;
export const ALLOWED_VISIBILITY = ["public", "private"] as const;
export const ALLOWED_SIZES = {
  small: {
    label: "Small",
    min: 1,
    max: 5_000,
  },
  medium: {
    label: "Medium",
    min: 5_000,
    max: 25_000,
  },
  large: {
    label: "Large",
    min: 25_000,
    max: 100_000,
  },
  xlarge: {
    label: "Very Large",
    min: 100_000,
    max: null,
  },
} as const;

export type SpaceType = (typeof ALLOWED_SPACE_TYPES)[number];
export type ProjectType = (typeof ALLOWED_PROJECT_TYPES)[number];
export type ProjectScope = (typeof ALLOWED_PROJECT_SCOPES)[number];
export type UnitSystem = (typeof ALLOWED_UNIT_SYSTEMS)[number];
export type Visibility = (typeof ALLOWED_VISIBILITY)[number];

export type NewProjectFormFields = {
  activeStep: number;
  projectName: string;
  projectDescription: string;
  location: Location;
  spaceType: SpaceType;
  projectType: ProjectType;
  projectScopes: ProjectScope[];
  size: keyof typeof ALLOWED_SIZES;
  unitSystem: "imperial" | "metric";
  visibility: Visibility;
  floorplans: string[];
  projectStartDate: string;
};

export const requiredFields = ["projectName", "location"];

const initialFormikValues: NewProjectFormFields = {
  activeStep: 0,
  projectName: "",
  projectDescription: "",
  location: DEFAULT_LOCATION,
  spaceType: "residential",
  projectType: "under construction",
  projectScopes: ["Visual documentation / Progress Tracking"],
  size: "medium",
  unitSystem: "imperial",
  visibility: "private",
  floorplans: [],
  projectStartDate: "",
};

const validationSchema = Yup.object().shape({
  projectName: Yup.string().required("Project name is required"),
  projectDescription: Yup.string()
    .max(255, "Project description cannot be longer than 255 characters")
    .optional(),
});

export const unitSystems: Record<UnitSystem, string> = {
  imperial: "Sqft.",
  metric: "Sqm.",
};

const PROJECT_YOUTUBE_TUTORIALS: Record<string, string | null> = {
  "Project Details": null, // for the first step
  "Add Floors": null, // for the second step
  "Upload Assets": null, // for the third step
  // "Create Project": "FKUx9yxMv84?si=jMlEsAsvpnbK2WnM", // for the create project whole process
  "Create Project": null, // for the create project whole process
};

// New Project Component
const NewProject = (props: Props) => {
  const [tutorialModalOpen, setTutorialModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [floors] = useAtom(newProjectFloorsAtom);
  const [uploadProgress, setUploadProgress] = useAtom(uploadProgressAtom);
  const [isUploading, setIsUploading] = useAtom(isUploadingProjectAssetsAtom);
  const [uppy, setUppy] = useAtom(newProjectUppyInstanceAtom);

  const newProjectIDRef = useRef<string | undefined>();

  const { activeWorkspace } = useWorkspaces();
  const { workspaceSlug } = useParams();
  const posthog = usePostHog();

  const hasSufficientCredits = activeWorkspace.availableCredits > 0;

  const locationInputRef = useRef<HTMLInputElement | null>(null);

  const { mutate: createProject, isPending: isCreatingProject } =
    useCreateProjectMutation();

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <ProjectDetailsForm locationInputRef={locationInputRef} />;
      case 1:
        return <AddFloors />;
      default:
        return null;
    }
  };

  const submit = async (values: NewProjectFormFields) => {
    if (!activeWorkspace.id || !workspaceSlug) return;

    if (activeWorkspace.availableCredits <= 0) {
      posthog.capture("project_creation_blocked_insufficient_credits", {
        workspace_id: activeWorkspace.id,
        workspace_slug: workspaceSlug,
        available_credits: activeWorkspace.availableCredits,
      });
      toast.error("You have insufficient credits to create a new project");
      return;
    }

    // if (floors.length === 0) {
    //   toast.error("Please add at least one floor");
    //   return;
    // }

    // setIsUploading(true);
    
    // Initialize upload progress
    // setUploadProgress(
    //   floors.map(floor => ({
    //     fileName: floor.s3Url,
    //     progress: 0,
    //     uploaded: false
    //   }))
    // );

    // try {
    //   // Upload all floor files first
    //   for (const floor of floors) {
    //     const file = uppy?.getFiles().find(f => f.name === floor.s3Url);
    //     if (!file) continue;

    //     await new Promise((resolve, reject) => {
    //       uppy?.upload().then((result) => {
    //         if (result?.failed?.length && result?.failed?.length > 0) {
    //           reject(new Error("Failed to upload file"));
    //         } else {
    //           setUploadProgress(prev => 
    //             prev.map(p => 
    //               p.fileName === floor.s3Url 
    //                 ? { ...p, uploaded: true, progress: 100 } 
    //                 : p
    //             )
    //           );
    //           resolve(result);
    //         }
    //       });
    //     });
    //   }

      // Create project after all uploads are complete
      const response = await createProject(
        {
          name: values.projectName,
          description: values.projectDescription,
          workspaceId: activeWorkspace.id || workspaceSlug,
          metadata: {
            spaceType: values.spaceType,
            location: values.location,
            projectScopes: values.projectScopes,
            size: values.size,
            unitSystem: values.unitSystem,
            projectType: values.projectType,
            projectStartDate: values.projectStartDate,
          // floorplans: finalFilesMetadata,
          },
          makePublic: values.visibility === "public",
        },
        {
          onSuccess(project) {
            posthog.capture("project_created", {
              project_id: project?.id,
              project_name: values.projectName,
              workspace_id: activeWorkspace.id,
              workspace_slug: workspaceSlug,
              space_type: values.spaceType,
              project_type: values.projectType,
              project_scopes: values.projectScopes,
              size: values.size,
              unit_system: values.unitSystem,
              visibility: values.visibility,
            });
            toast.success("Project Created Successfully!");
            navigate(`/${workspaceSlug}`);
          },
          onError(error) {
            toast.error("Failed to create project");
            setIsUploading(false);
          },
        }
      );
    // } catch (error) {
    //   toast.error("Failed to create project");
    //   setIsUploading(false);
    // }
  };

  const navigate = useNavigate();

  const onClose = () => {
    navigate("..", { relative: "path" });
  };

  return (
    <Formik
      initialValues={initialFormikValues}
      validationSchema={validationSchema}
      onSubmit={(values) => submit(values)}
    >
      {({ values, setFieldValue, handleSubmit, errors, touched }) => {
        const { activeStep, projectName } = values;

        const isNextDisabled = () => {
          if (activeWorkspace.availableCredits <= 0) return true;
          if (values.activeStep === 0 && !projectName) {
            return true;
          }
          return Object.keys(errors).some(
            (key) => touched[key as keyof NewProjectFormFields]
          );
        };

        useNewProjectLocationGeocoder(values, setFieldValue);

        return (
          <Stack spacing={2}>
            <Box sx={styles.container}>
              {!hasSufficientCredits && (
                <FullWidthBanner
                  type="error"
                  text="You have insufficient credits to create a new project"
                />
              )}
              <Box sx={styles.titleBar}>
                <Box sx={styles.titleBarLeft}>
                  <Box>
                    <Typography variant="h6" sx={styles.title}>
                      New Project
                    </Typography>
                    <Typography variant="subtitle1" sx={styles.subtitle}>
                      {STEPS[activeStep].label}
                    </Typography>
                  </Box>
                </Box>

                {PROJECT_YOUTUBE_TUTORIALS["Create Project"] && (
                  <Box sx={styles.tutorialBar}>
                    <Box sx={styles.tutorialBtn}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ display: {xs: "none", sm: "block"}}} 
                      >
                        Show me how
                      </Typography>
                      <IconButton
                        aria-label="tutorial"
                        onClick={() => {
                          setTutorialModalOpen(true);
                        }}
                      >
                        <HelpOutlineIcon />
                      </IconButton>
                    </Box>
                    <TutorialModal
                      open={tutorialModalOpen}
                      name={"How to Create a Project"}
                      link={PROJECT_YOUTUBE_TUTORIALS["Create Project"]}
                      onClose={() => setTutorialModalOpen(false)}
                    />
                  </Box>
                )}

                <Stepper activeStep={activeStep}>
                  {STEPS.map((label, index) => (
                    <Step key={index}>
                      <StepButton
                        disableRipple
                        onClick={() => setFieldValue("activeStep", index)}
                      ></StepButton>
                    </Step>
                  ))}
                </Stepper>
              </Box>
              <Divider sx={styles.divider} />

              <Box sx={styles.stepContainer}>{getStepContent(activeStep)}</Box>

              <Box sx={styles.actionButtons}>
                <Button
                  onClick={() => setConfirmOpen(true)}
                  startIcon={<DeleteIcon />}
                  color="error"
                >
                  Cancel
                </Button>
                <Box sx={styles.rightButtons}>
                  <Button
                    variant="text"
                    disabled={activeStep === 0 || isUploading}
                    onClick={() => setFieldValue("activeStep", activeStep - 1)}
                    sx={styles.backActionButton}
                  >
                    Back
                  </Button>
                  <LoadingButton
                    loading={isUploading}
                    variant="contained"
                    onClick={() =>
                      activeStep === STEPS.length - 1
                        ? handleSubmit()
                        : setFieldValue("activeStep", activeStep + 1)
                    }
                    disabled={isNextDisabled()}
                  >
                    {activeStep === STEPS.length - 1 ? "Create Project" : "Next"}
                  </LoadingButton>
                </Box>
              </Box>

              <ConfirmDialog
                open={confirmOpen}
                onClose={() => {
                  setConfirmOpen(false);
                }}
                onConfirm={onClose}
                title="Are you sure?"
              >
                <Typography>
                  Any unsaved changes that you made will be lost.
                </Typography>
              </ConfirmDialog>
            </Box>
            {isUploading && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Uploading Files...
                </Typography>
                {uploadProgress.map((progress) => (
                  <Box
                    key={progress.fileName}
                    sx={{
                      mb: 1,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography>{progress.fileName}</Typography>
                      <Typography>
                        {progress.uploaded ? "Completed" : `${Math.round(progress.progress)}%`}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={progress.progress}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Stack>
        );
      }}
    </Formik>
  );
};

export default NewProject;
